import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { DashboardOverviewComponent } from './dashboard/dashboard-overview/dashboard-overview.component';
import { ProjectOverviewComponent } from './project/project-overview/project-overview.component';
import { UserDataSettingsComponent } from './user/user-data-settings/user-data-settings.component';
import { LoginOverviewComponent } from './user/login-overview/login-overview.component';
import { AccountResetPasswordComponent } from './user/account-reset-password/account-reset-password.component';
import { LoginGuard } from './user/shared/login.guard';

import { ProjectGooglemapsComponent } from './project/project-googlemaps/project-googlemaps.component';
import { ProjectSettingsOverviewComponent } from './project/project-settings/project-settings-overview.component';
import { ProjectSchemaComponent } from './project/project-schema/project-schema.component';
import { ProjectTestAreaComponent } from './project/project-test-area/project-test-area.component';
import { ProjectStationOverviewComponent } from './project/project-station-overview/project-station-overview.component';
import { ProjectDocumentsComponent } from './project/project-documents/project-documents.component';
import { ProjectNotesComponent } from './project/project-notes/project-notes.component';
import { LayoutComponent } from './user/layout/layout.component';
import { UserResolver } from './user/shared/user.resolver';
import { DeviceOverviewComponent } from "./device/device-overview/device-overview.component";
import { DeviceTestAreaComponent } from "./device/device-test-area/device-test-area.component";
import { DeviceSettingsComponent } from "./device/device-settings/device-settings.component";
import { ProjectTimeSwitchComponent } from './project/project-time-switch/project-time-switch.component';
import { ProjectTimeSwitchNeuComponent } from './project/project-time-switch-neu/project-time-switch-neu.component';
import { CompanyOverviewComponent } from "./company/company/company-overview/company-overview.component";
import { CompanySettingsComponent } from "./company/company/company-settings/company-settings.component";
import { UserOverviewComponent } from "./user/user-overview/user-overview.component";
import { UserSettingsComponent } from "./user/user-settings/user-settings.component";
import { UserComponent } from "./user/user/user.component";
import { CompanyComponent } from "./company/company/company.component";
import { ProjectImagesComponent } from "./project/project-images/project-images.component";
import { RoleGuard } from "./user/shared/role.guard";
import { ProjectSwaConfigComponent } from './project/project-swa-config/project-swa-config.component';
import { ProjectSwaVisualisierungComponent } from './project/project-swa-visualisierung/project-swa-visualisierung.component';
import { DeviceRouteStationComponent } from "./device/device-route-station/device-route-station.component";
import { FaqComponent } from "./core/faq/faq.component";
import { FaqAccountComponent } from './core/faq/faq-account/faq-account.component';
import { FaqOverviewComponent } from './core/faq/faq-overview/faq-overview.component';
import { FaqDashboardComponent } from './core/faq/faq-dashboard/faq-dashboard.component';
import { FaqProjectComponent } from './core/faq/faq-project/faq-project.component';
import { FaqReportComponent } from './core/faq/faq-report/faq-report.component';
import { FaqBilderComponent } from './core/faq/faq-admin-overview/faq-bilder/faq-bilder.component';
import { FaqSwaComponent } from './core/faq/faq-admin-overview/faq-swa/faq-swa.component';
import { FaqTimerSwitchComponent } from './core/faq/faq-admin-overview/faq-timer-switch/faq-timer-switch.component';
import { ImpressumComponent } from './core/impressum/impressum.component';
import { PrivacyPolicyComponent } from './core/privacy-policy/privacy-policy.component';
import { ProjectScenarioComponent } from './project/project-scenario/project-scenario.component';
import { ReportOverviewNewComponent } from './report/report-overview-new/report-overview-new.component';
import { DeviceNotesComponent } from './device/device-notes/device-notes.component';
import { DetailedReportingsComponent } from './report/detailed-reportings/detailed-reportings.component';
import { DeviceMapComponent } from './device/device-map/device-map.component';
import { ErrorEngineOverviewComponent } from './error-engine/error-engine-overview/error-engine-overview.component';
import { ErrorMessagesComponent } from './error-engine/error-messages/error-messages.component';
import { ProjectTrafficMonitoringComponent } from './project/project-traffic-monitoring/project-traffic-monitoring.component';
import { ReportingOverviewComponent } from './reporting/reporting-overview/reporting-overview.component';
import { ReportingMainPageComponent } from './reporting/reporting-main-page/reporting-main-page.component';
import { environment } from 'src/environments/environment';

const routes: Routes = [
  {
    path: '', component: LayoutComponent, canActivate: [LoginGuard], resolve: { userData: UserResolver },
    children: [
      { path: '', redirectTo: 'project', pathMatch: 'full' },
      { path: 'dashboard', component: DashboardOverviewComponent, canActivate: [LoginGuard, RoleGuard] },
      {
        path: 'project', component: ProjectOverviewComponent, canActivate: [LoginGuard],
        children: [
          { path: '', redirectTo: environment.googleMapsEnabled ? 'map' : 'station-overview', pathMatch: 'full' },

          environment.googleMapsEnabled
            ? { path: 'map', component: ProjectGooglemapsComponent, canActivate: [RoleGuard] }
            : { path: 'map', redirectTo: 'station-overview', pathMatch: 'full' },
          { path: 'settings', component: ProjectSettingsOverviewComponent, canActivate: [RoleGuard] },
          { path: 'schema', component: ProjectSchemaComponent, canActivate: [RoleGuard] },
          { path: 'test-area', component: ProjectTestAreaComponent, canActivate: [RoleGuard] },
          { path: 'station-overview', component: ProjectStationOverviewComponent, canActivate: [RoleGuard] },
          { path: 'images', component: ProjectImagesComponent, canActivate: [RoleGuard] },
          { path: 'documents', component: ProjectDocumentsComponent, canActivate: [RoleGuard] },
          { path: 'notes', component: ProjectNotesComponent, canActivate: [RoleGuard] },
          { path: 'time-switch', component: ProjectTimeSwitchComponent, canActivate: [RoleGuard] },
          { path: 'time-switch-neu', component: ProjectTimeSwitchNeuComponent, canActivate: [RoleGuard] },
          { path: 'swa-config', component: ProjectSwaConfigComponent, canActivate: [RoleGuard] },
          { path: 'swa-visualisierung', component: ProjectSwaVisualisierungComponent, canActivate: [RoleGuard] },
          { path: 'scenario', component: ProjectScenarioComponent, canActivate: [RoleGuard] },
          { path: 'traffic-monitoring', component: ProjectTrafficMonitoringComponent, canActivate: [RoleGuard] }
        ]
      },
      {
        path: 'device', component: DeviceOverviewComponent, canActivate: [LoginGuard, RoleGuard],
        children: [
          { path: '', redirectTo: 'settings', pathMatch: 'full' },
          // Geräte-Karte ist deaktiviert (Tab-Link in device-overview auskommentiert,
          // Route nur per Deep-Link erreichbar): GetAllRouteStationsDeviceParamsShort
          // hatte Performance-Probleme; vor einer Reaktivierung neue Route entwickeln.
          environment.googleMapsEnabled
            ? { path: 'map', component: DeviceMapComponent, canActivate: [RoleGuard] }
            : { path: 'map', redirectTo: 'settings', pathMatch: 'full' },
          { path: 'settings', component: DeviceSettingsComponent, canActivate: [RoleGuard] },
          { path: 'test-area', component: DeviceTestAreaComponent, canActivate: [RoleGuard] },
          { path: 'route-station', component: DeviceRouteStationComponent, canActivate: [RoleGuard] },
          { path: 'notes', component: DeviceNotesComponent, canActivate: [RoleGuard] }
        ]
      },
      {
        path: 'report', component: ReportOverviewNewComponent, canActivate: [LoginGuard],
        children: [
          { path: '', redirectTo: 'detailed', pathMatch: 'full' },
          { path: 'detailed', component: DetailedReportingsComponent, canActivate: [RoleGuard] },
        ]
      },

      /* new area */

      {
        path: 'reporting', component: ReportingOverviewComponent, canActivate: [LoginGuard],
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          { path: 'overview', component: ReportingMainPageComponent, canActivate: [RoleGuard] },
        ]
      },

      /* new area end */

      {
        path: 'error-engine', component: ErrorEngineOverviewComponent, canActivate: [LoginGuard],
        children: [
          { path: '', redirectTo: 'messages', pathMatch: 'full' },
          { path: 'messages', component: ErrorMessagesComponent, canActivate: [RoleGuard] },
        ]
      },
      {
        path: 'user', component: UserComponent, canActivate: [LoginGuard],
        children: [
          { path: '', redirectTo: 'user-overview', pathMatch: 'full' },
          { path: 'user-data-settings', component: UserDataSettingsComponent, canActivate: [RoleGuard] },
          { path: 'user-overview', component: UserOverviewComponent, canActivate: [RoleGuard] },
          { path: 'edit/:id', component: UserSettingsComponent },
          { path: 'create', component: UserSettingsComponent, canActivate: [RoleGuard] },
        ]
      },
      {
        path: 'company', component: CompanyComponent, canActivate: [LoginGuard, RoleGuard],
        children: [
          { path: '', redirectTo: 'company-overview', pathMatch: 'full' },
          { path: 'company-overview', component: CompanyOverviewComponent },
          { path: 'edit/:id', component: CompanySettingsComponent },
          { path: 'create', component: CompanySettingsComponent },
        ]
      },
      {
        path: 'faq', component: FaqOverviewComponent, canActivate: [RoleGuard],
        children: [
          { path: '', redirectTo: 'faq', pathMatch: 'full' },
          { path: 'faq', component: FaqComponent },
          { path: 'account', component: FaqAccountComponent },
          { path: 'overview', component: FaqDashboardComponent },
          { path: 'project', component: FaqProjectComponent },
          { path: 'report', component: FaqReportComponent },
          { path: 'bilder', component: FaqBilderComponent, canActivate: [RoleGuard] },
          { path: 'swa', component: FaqSwaComponent, canActivate: [RoleGuard] },
          { path: 'timer-switch', component: FaqTimerSwitchComponent, canActivate: [RoleGuard] },
        ]
      }
    ]
  },
  { path: 'login', component: LoginOverviewComponent },
  // Target of the link in the API's account emails. Public: the user may not have a password yet.
  { path: 'account/reset-password', component: AccountResetPasswordComponent },
  { path: 'privacy', component: PrivacyPolicyComponent },
  { path: 'impressum', component: ImpressumComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
